import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { BlockRenderer } from '../../renderer/BlockRenderer.js';
import { SCHEME_COLOR_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
import { MaterialIcon, MaterialSymbolsFont } from '../_shared/materialSymbols.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
/**
 * `benefit-panel` — a GENERIC buy-box panel card (the top-tier Shopify PDP anatomy: a soft rounded
 * surface holding an optional reviews row followed by N one-line icon+text benefit rows, collapsing
 * past a threshold behind a fade + "View more" toggle).
 *
 * WHY A NEW BLOCK (config-first ladder, escalated): the existing `emoji-benefits` block is a VERBATIM
 * Shrine port whose whole authoring model is ONE richtext where each `<p>` line is an emoji + text —
 * no per-row icon picker, no reviews row, no collapse. Expressing this panel there would REPLACE its
 * model (structured rows + icon-name fields + a reviews sub-component), not extend it, and would break
 * the "Shrine CSS verbatim" fidelity contract that block carries. So: new block, generic settings.
 *
 * Anatomy (all optional / config-driven):
 *  - Reviews row: star icons (same overlay-clip technique as the decorative `rating` block, sourced
 *    from THIS block's own `rating`/`starColor` settings) + a free label + an optional chevron; when
 *    `reviewsScrollId` is set the row is an anchor to `#<id>` (e.g. a reviews section below).
 *  - Benefit rows: `benefit-row` CHILD blocks (unlimited, add / remove / reorder in the left tree, each
 *    with its own inspector) — a Material Symbols icon NAME (+ optional FILL, + optional image override)
 *    + one line of text. Shared size/colour flow from this panel via inherited CSS vars; the panel owns
 *    each row's `<li>` (spacing + collapse). Legacy flat rows (icon1/text1…) still render as a fallback
 *    and are auto-migrated to child blocks on load (hydrate.ts). A row renders only when its text is set.
 *  - Collapse: when `collapseAfter` > 0 and more rows than it are visible, rows past the threshold
 *    hide behind a bottom fade + a "Voir plus / Voir moins" toggle. NO-JS SAFE (SSR): a visually
 *    hidden checkbox + `:checked ~` sibling CSS — the same idiom the featured-collection tabs use —
 *    so the toggle works on the static storefront with zero client JS. `collapseAfter: 0` (default)
 *    disables collapsing entirely.
 *
 * Product buy-box block: reads the shared product state and renders nothing outside a product
 * container (same gate as emoji-benefits / icon-with-text).
 */
const ICON_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'outline-button', label: 'Outline button' },
    { value: 'text', label: 'Text' },
];
/**
 * Row defaults — ONE source for the schema `default`s AND the render-side fallbacks, so a block with
 * absent settings (fresh drop, no builder materialisation) renders exactly what the inspector shows.
 */
// Only the ICON is a default now. The three sentences that used to sit here were dead: the render below
// reads the stored text with no fallback ON PURPOSE, so a fresh panel prompts "Add block" instead of
// publishing invented copy. Keeping them meant three French sentences that no shop could ever show, and a
// census that could not tell them from live ones.
const ROW_DEFAULTS = [
    { icon: 'local_shipping' },
    { icon: 'verified_user' },
    { icon: 'payments' },
    { icon: 'check_circle' },
    { icon: 'check_circle' },
    { icon: 'check_circle' },
];
export const benefitPanelBlockSchema = {
    type: 'benefit-panel',
    name: 'Benefit panel',
    // The benefit ROWS are `benefit-row` CHILD blocks (add / remove / reorder in the tree, each with its
    // own inspector) — no fixed per-row fields live here any more. Legacy flat rows (icon1/text1…) still
    // RENDER as a fallback and are auto-migrated to children on load (hydrate.ts `migrateBenefitPanel`).
    accepts: ['benefit-row'],
    fields: [
        visibilityField,
        // ── Reviews row (optional, decorative — same model as the `rating` block) ──────────────────────
        {
            id: 'showReviews',
            type: 'checkbox',
            label: 'Show reviews row',
            group: 'Reviews',
            default: false,
        },
        {
            id: 'rating',
            type: 'range',
            label: 'Rating',
            group: 'Reviews',
            min: 1,
            max: 5,
            step: 0.1,
            default: 5,
            visibleWhen: { field: 'showReviews', equals: true },
        },
        {
            id: 'reviewsLabel',
            type: 'text',
            label: 'Reviews text',
            group: 'Reviews',
            default: '1024 avis',
            maxLength: 60,
            visibleWhen: { field: 'showReviews', equals: true },
        },
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            group: 'Reviews',
            default: { ref: 'theme', token: 'star' },
            visibleWhen: { field: 'showReviews', equals: true },
        },
        {
            id: 'showReviewsChevron',
            type: 'checkbox',
            label: 'Show chevron',
            group: 'Reviews',
            default: true,
            visibleWhen: { field: 'showReviews', equals: true },
        },
        {
            id: 'reviewsScrollId',
            aiWritable: false, // 0059 — machine format, not prose
            type: 'text',
            label: 'Scroll to ID',
            group: 'Reviews',
            info: 'Optional anchor id — the reviews row links to #id (e.g. a reviews section below).',
            default: '',
            maxLength: 60,
            visibleWhen: { field: 'showReviews', equals: true },
        },
        // ── Benefit rows ───────────────────────────────────────────────────────────────────────────────
        {
            id: 'iconColor',
            aiConfigurable: true,
            type: 'select',
            label: 'Icon color',
            group: 'Content',
            default: 'text',
            options: ICON_COLOR_OPTIONS,
        },
        // ── Collapse (View more) ───────────────────────────────────────────────────────────────────────
        {
            id: 'collapseAfter',
            type: 'range',
            label: 'Collapse after',
            group: 'Collapse',
            info: 'Rows shown before the "View more" fold. 0 = always show every row.',
            min: 0,
            max: 6,
            step: 1,
            default: 0,
        },
        {
            id: 'viewMoreLabel',
            type: 'text',
            label: '"View more" label',
            group: 'Collapse',
            default: 'Voir plus',
            defaultKey: 'common.show_more',
            maxLength: 40,
        },
        {
            id: 'viewLessLabel',
            type: 'text',
            label: '"View less" label',
            group: 'Collapse',
            default: 'Voir moins',
            defaultKey: 'common.show_less',
            maxLength: 40,
        },
        // ── Styling ────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'panelColorScheme',
            aiConfigurable: true,
            type: 'select',
            label: 'Panel background',
            group: 'Styling',
            default: 'background-2',
            options: SCHEME_COLOR_OPTIONS,
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Styling',
            min: 0,
            max: 40,
            step: 2,
            default: 16,
            unit: 'px',
        },
        {
            id: 'panelPadding',
            type: 'range',
            label: 'Panel padding',
            group: 'Styling',
            min: 8,
            max: 40,
            step: 2,
            default: 20,
            unit: 'px',
        },
        {
            id: 'rowSpacing',
            type: 'range',
            label: 'Row spacing',
            group: 'Styling',
            min: 4,
            max: 24,
            step: 2,
            default: 12,
            unit: 'px',
        },
        {
            id: 'iconSize',
            type: 'range',
            label: 'Icon size',
            group: 'Styling',
            min: 14,
            max: 40,
            step: 2,
            default: 20,
            unit: 'px',
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Styling',
            min: 12,
            max: 20,
            step: 1,
            default: 14,
            unit: 'px',
        },
        ...marginFields(15, 15),
    ],
};
// Star SVG (same Shrine path as the `rating` block — the coloured overlay is clipped to the rating
// width over the muted underlay so a fractional rating shows precisely). Kept local: `rating` doesn't
// export its private glyph and a `_shared` extraction is not warranted for one constant.
const STAR_FULL = 'M259.3 17.8 194 150.2 47.9 171.4c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46.4 46.4 33.7L288 439.6l130.7 68.7c23.2 12.1 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.7-57.4 0z';
function Stars({ fill }) {
    const svg = { width: '1em', height: '1em', flexShrink: 0, fill };
    return (_jsx(_Fragment, { children: [0, 1, 2, 3, 4].map((i) => (_jsx("svg", { viewBox: "0 0 576 512", "aria-hidden": "true", style: { ...svg, marginInlineStart: i === 0 ? 0 : '0.125em' }, children: _jsx("path", { d: STAR_FULL }) }, i))) }));
}
/** Resolve a stored color value: custom hex, allowlisted theme token → live var, else the fallback. */
function resolveHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (typeof c.value === 'string')
            return c.value;
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string')
        return v;
    return fallback;
}
function ChevronDown() {
    return (_jsx("svg", { width: "12", height: "8", viewBox: "0 0 12 8", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M1 1.5 6 6.5l5-5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
function ChevronRight() {
    return (_jsx("svg", { width: "8", height: "12", viewBox: "0 0 8 12", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M1.5 1 6.5 6l-5 5", stroke: "currentColor", strokeWidth: "1.6", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
const BenefitPanelBlock = ({ block, context }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-bp-${block.id}`;
    // Benefit rows are `benefit-row` CHILD blocks (what the seller edits in the tree). A LEGACY panel that
    // still carries only the flat icon1/text1… settings falls back to rendering those — but hydrate.ts
    // migrates flat → children on load, so this fallback is a belt-and-braces safety net, never the norm.
    const childBlocks = (block.blocks ?? []).filter((b) => !b.disabled && str(b.settings?.text).trim().length > 0);
    const useChildren = childBlocks.length > 0;
    // Legacy flat rows: read ONLY stored values (no ROW_DEFAULTS text) so a FRESH panel with no children
    // renders empty (prompting "Add block"), while a pre-migration stored panel still shows its rows.
    const flatRows = useChildren
        ? []
        : ROW_DEFAULTS.map((d, i) => {
            const n = i + 1;
            return {
                n,
                icon: str(bs[`icon${n}`], d.icon),
                filled: bs[`icon${n}Fill`] === true,
                image: bs[`image${n}`],
                text: str(bs[`text${n}`]),
            };
        }).filter((r) => r.text.trim().length > 0);
    const rowCount = useChildren ? childBlocks.length : flatRows.length;
    const showReviews = bs.showReviews === true;
    // Nothing to show: drop on the LIVE store, but KEEP an empty panel in the builder so it stays
    // selectable on the canvas and its "Add block" affordance is reachable.
    if (rowCount === 0 && !showReviews && context.renderMode !== 'edit')
        return null;
    const iconColor = schemeColor(bs.iconColor ?? 'text');
    const iconSize = num(bs.iconSize, 20);
    const textSize = num(bs.textSize, 14);
    const rowGap = num(bs.rowSpacing, 12);
    const radius = num(bs.cornerRadius, 16);
    const pad = num(bs.panelPadding, 20);
    const panelBg = schemeColor(bs.panelColorScheme ?? 'background-2');
    // Collapse: active only when asked (collapseAfter > 0) AND there are more rows than the threshold.
    const collapseAfter = Math.max(0, Math.round(num(bs.collapseAfter, 0)));
    const collapsible = collapseAfter > 0 && rowCount > collapseAfter;
    const toggleId = `${cls}-more`;
    // Reviews row.
    const rating = Math.min(5, Math.max(1, num(bs.rating, 5)));
    const reviewsLabel = str(bs.reviewsLabel, '1024 avis');
    const starColor = resolveHex(bs.starColor, '#ffcc00');
    const showChevron = bs.showReviewsChevron !== false;
    const scrollId = str(bs.reviewsScrollId).trim();
    const visibleStars = Math.ceil(rating) || 1;
    const overlayWidth = `calc(${rating} * 1em + ${visibleStars - 1} * 0.125em)`;
    // ── CSS (SSR/no-JS safe) ─────────────────────────────────────────────────────────────────────────
    // The checkbox + `:checked ~` sibling reveal is the featured-collection tabs idiom: the input is
    // visually hidden but KEPT FOCUSABLE, the <label> is the visible toggle, and every state change is
    // pure CSS — the collapse works with client JS fully disabled (static storefront SSR).
    let css = `.${cls}__panel{background:${panelBg};border-radius:${radius}px;padding:${pad}px;` +
        `font-family:var(--aqx-theme-font)}` +
        (showReviews
            ? `.${cls}__reviews{display:flex;align-items:center;gap:8px;margin:0 0 ${rowGap}px;` +
                `color:var(--aqx-scheme-text);text-decoration:none;font-size:${textSize}px;` +
                `font-weight:600;line-height:1}`
            : '') +
        `.${cls}__list{list-style:none;margin:0;padding:0;position:relative;display:flex;` +
        `flex-direction:column;gap:${rowGap}px}` +
        `.${cls}__row{display:flex;align-items:center;gap:10px;font-size:${textSize}px;` +
        `line-height:1.45;color:var(--aqx-scheme-text)}`;
    if (collapsible) {
        css +=
            // Visually hidden but focusable checkbox (keyboard operable through the label).
            `.${cls}__toggle{position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;` +
                `clip:rect(0 0 0 0);overflow:hidden;white-space:nowrap}` +
                `.${cls}__row--extra{display:none}` +
                `.${cls}__toggle:checked~.${cls}__list .${cls}__row--extra{display:flex}` +
                // Bottom fade over the last visible rows while collapsed (fades to the panel background).
                `.${cls}__list::after{content:'';position:absolute;left:0;right:0;bottom:0;height:${Math.max(28, rowGap * 3)}px;` +
                `background:linear-gradient(to bottom,transparent,${panelBg});pointer-events:none}` +
                `.${cls}__toggle:checked~.${cls}__list::after{content:none}` +
                `.${cls}__more{display:inline-flex;align-items:center;gap:6px;margin-top:${rowGap}px;` +
                `cursor:pointer;font-size:${textSize}px;font-weight:600;color:var(--aqx-scheme-text)}` +
                `.${cls}__more svg{transition:transform .25s ease}` +
                `.${cls}__less{display:none}` +
                `.${cls}__toggle:checked~.${cls}__more .${cls}__vmore{display:none}` +
                `.${cls}__toggle:checked~.${cls}__more .${cls}__less{display:inline}` +
                `.${cls}__toggle:checked~.${cls}__more svg{transform:rotate(180deg)}` +
                `.${cls}__toggle:focus-visible~.${cls}__more{outline:2px solid var(--aqx-scheme-accent);outline-offset:2px}`;
    }
    css += visibilityCss(cls, bs.visibility);
    const ReviewsTag = scrollId ? 'a' : 'div';
    const reviewsRow = showReviews ? (_jsxs(ReviewsTag, { className: `${cls}__reviews`, ...(scrollId ? { href: `#${scrollId}` } : {}), children: [_jsxs("span", { "aria-label": `Note ${rating} sur 5`, role: "img", style: { position: 'relative', display: 'inline-flex', lineHeight: 1 }, children: [_jsx("span", { style: { display: 'flex' }, children: _jsx(Stars, { fill: "var(--aqx-scheme-border)" }) }), _jsx("span", { style: {
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            width: overlayWidth,
                        }, children: _jsx(Stars, { fill: starColor }) })] }), reviewsLabel ? _jsx("span", { children: reviewsLabel }) : null, showChevron ? (_jsx("span", { "aria-hidden": "true", style: { display: 'inline-flex', opacity: 0.7 }, children: _jsx(ChevronRight, {}) })) : null] })) : null;
    return (_jsxs(_Fragment, { children: [rowCount > 0 ? _jsx(MaterialSymbolsFont, {}) : null, _jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: marginStyle(bs, 15, 15), children: _jsxs("div", { className: `${cls}__panel`, children: [reviewsRow, collapsible ? (
                        // Sibling ORDER matters: the checkbox must precede the list + toggle label for `~` to match.
                        _jsx("input", { type: "checkbox", id: toggleId, className: `${cls}__toggle` })) : null, rowCount > 0 ? (_jsx("ul", { className: `${cls}__list`, 
                            // Icon size + colour flow to the `benefit-row` CHILD blocks through these inherited vars, so
                            // every row stays consistent without per-row size/colour controls (text size rides the
                            // `.__row` font-size). The legacy flat rows below read the same iconSize/iconColor directly.
                            style: {
                                '--aqx-icon-size': `${iconSize}px`,
                                '--aqx-bp-icon-color': iconColor,
                            }, children: useChildren
                                ? childBlocks.map((child, i) => (
                                // The panel owns the `<li>` (row spacing + the "View more" `--extra` collapse); the
                                // child block renders only the row's icon + text.
                                _jsx("li", { className: `${cls}__row${collapsible && i >= collapseAfter ? ` ${cls}__row--extra` : ''}`, children: _jsx(BlockRenderer, { block: child, context: context }) }, child.id)))
                                : flatRows.map((r, i) => (_jsxs("li", { className: `${cls}__row${collapsible && i >= collapseAfter ? ` ${cls}__row--extra` : ''}`, children: [_jsx("span", { style: { flex: '0 0 auto', lineHeight: 0 }, children: _jsx(MaterialIcon, { name: r.icon, filled: r.filled, image: r.image, size: iconSize, color: iconColor }) }), _jsx("span", { children: r.text })] }, r.n))) })) : null, collapsible ? (_jsxs("label", { htmlFor: toggleId, className: `${cls}__more`, children: [_jsx("span", { className: `${cls}__vmore`, children: str(bs.viewMoreLabel, 'Voir plus') }), _jsx("span", { className: `${cls}__less`, children: str(bs.viewLessLabel, 'Voir moins') }), _jsx(ChevronDown, {})] })) : null] }) })] }));
};
export const benefitPanelBlock = {
    type: 'benefit-panel',
    schema: benefitPanelBlockSchema,
    Component: BenefitPanelBlock,
    meta: { label: 'Benefit panel' },
};
//# sourceMappingURL=index.js.map